/**
 * Canonical, serializable environment state (spec §5.1). This is the single source of truth for
 * time, date/season, and weather. Rendering models (sun, season, ground) derive their behaviour
 * from this state; the state itself holds no Three.js objects.
 */

import {
  DEFAULT_ICE_SWATCH,
  DEFAULT_SNOW_SWATCH,
  isIceSwatchId,
  isSnowSwatchId,
  type IceSwatchId,
  type SnowSwatchId,
} from '@/terrain/WinterGround';

/**
 * Bumped to 2 for {@link GroundCoverState}. Older documents deserialize to bare ground, which is
 * what they were drawn as — there is no cover to infer from a file that predates the idea.
 */
export const ENVIRONMENT_SCHEMA_VERSION = 2;

/** Wall-clock time of day used to drive the manual sun model. */
export interface TimeOfDay {
  /** 0–23. */
  hour: number;
  /** 0–59. */
  minute: number;
}

export type SeasonMode = 'simple' | 'calendar';

export type SimpleSeason = 'spring' | 'summer' | 'autumn' | 'winter';

export const SIMPLE_SEASONS: readonly SimpleSeason[] = ['spring', 'summer', 'autumn', 'winter'];

/**
 * Date/season selection. In `simple` mode the discrete {@link SimpleSeason} drives appearance; in
 * `calendar` mode the continuous `dayOfYear` (1–365) is used for smooth interpolation.
 */
export interface DateState {
  mode: SeasonMode;
  season: SimpleSeason;
  /** 1–365. */
  dayOfYear: number;
}

export type WeatherPreset =
  | 'clear'
  | 'overcast'
  | 'golden-hour'
  | 'light-wind'
  | 'breezy'
  | 'after-rain';

export const WEATHER_PRESETS: readonly WeatherPreset[] = [
  'clear',
  'overcast',
  'golden-hour',
  'light-wind',
  'breezy',
  'after-rain',
];

/**
 * Winter ground cover: snow and ice lying over the whole site.
 *
 * The two are independent because they are independent outdoors — a garden can be under fresh snow
 * with no ice in it, glazed with ice and no snow at all after a freezing rain, or snow lying over a
 * frozen crust, which is the commonest of the three in February. Ticking one must not untick the
 * other.
 *
 * Kept here rather than folded into {@link WeatherPreset} because the presets are about the *air* —
 * what the light is doing and how it is moving — and they are chosen freely at any date. Cover is
 * about the *ground*, it persists after the weather that laid it has gone, and it is only offered in
 * the freezing part of the year. See `isFreezingSeason`.
 *
 * The flags are stored whatever the date is, and gated on the season only when they are *applied*.
 * That is what lets the date be dragged through the summer and back without the cover having to be
 * set up again — a document that says "this site is under snow" keeps saying so.
 */
export interface GroundCoverState {
  snow: boolean;
  ice: boolean;
  /** Which snow to lay. See `SNOW_SWATCHES`. */
  snowSwatch: SnowSwatchId;
  /** Which ice to glaze with. See `ICE_SWATCHES`. */
  iceSwatch: IceSwatchId;
  /** 0–1. Below 1 the cover is broken up, from a full fall down to a dusting. */
  snowAmount: number;
  /** 0–1. Below 1 the glaze is patchy, the way ice goes as it lifts. */
  iceAmount: number;
}

export function defaultGroundCoverState(): GroundCoverState {
  return {
    snow: false,
    ice: false,
    snowSwatch: DEFAULT_SNOW_SWATCH,
    iceSwatch: DEFAULT_ICE_SWATCH,
    snowAmount: 1,
    iceAmount: 1,
  };
}

export interface EnvironmentState {
  schemaVersion: number;
  time: TimeOfDay;
  date: DateState;
  weather: WeatherPreset;
  /** Snow and ice over the ground. Only applied in the freezing part of the year. */
  groundCover: GroundCoverState;
  /** Site latitude in degrees, used by the sun model. */
  latitudeDegrees: number;
}

/** The default environment: a clear summer midday on bare ground. */
export function defaultEnvironmentState(): EnvironmentState {
  return {
    schemaVersion: ENVIRONMENT_SCHEMA_VERSION,
    time: { hour: 12, minute: 0 },
    date: { mode: 'simple', season: 'summer', dayOfYear: 172 },
    weather: 'clear',
    groundCover: defaultGroundCoverState(),
    latitudeDegrees: 45,
  };
}

function clamp(value: number, min: number, max: number): number {
  if (Number.isNaN(value)) return min;
  return Math.min(max, Math.max(min, value));
}

/** Serialize environment state to a plain JSON-safe object. */
export function serializeEnvironmentState(state: EnvironmentState): EnvironmentState {
  return {
    schemaVersion: ENVIRONMENT_SCHEMA_VERSION,
    time: { hour: state.time.hour, minute: state.time.minute },
    date: { mode: state.date.mode, season: state.date.season, dayOfYear: state.date.dayOfYear },
    weather: state.weather,
    groundCover: { ...state.groundCover },
    latitudeDegrees: state.latitudeDegrees,
  };
}

/**
 * Rebuild a fully-formed {@link EnvironmentState} from arbitrary parsed JSON, filling any missing or
 * out-of-range fields with defaults so downstream models always receive valid input.
 */
export function deserializeEnvironmentState(raw: unknown): EnvironmentState {
  const base = defaultEnvironmentState();
  if (typeof raw !== 'object' || raw === null) return base;
  const data = raw as Record<string, unknown>;

  const time = data.time as Record<string, unknown> | undefined;
  if (time) {
    base.time.hour = clamp(Math.trunc(Number(time.hour ?? base.time.hour)), 0, 23);
    base.time.minute = clamp(Math.trunc(Number(time.minute ?? base.time.minute)), 0, 59);
  }

  const date = data.date as Record<string, unknown> | undefined;
  if (date) {
    if (date.mode === 'simple' || date.mode === 'calendar') base.date.mode = date.mode;
    if (SIMPLE_SEASONS.includes(date.season as SimpleSeason)) {
      base.date.season = date.season as SimpleSeason;
    }
    base.date.dayOfYear = clamp(Math.trunc(Number(date.dayOfYear ?? base.date.dayOfYear)), 1, 365);
  }

  // A `wind` block from a document written before wind was removed is read and dropped.

  if (WEATHER_PRESETS.includes(data.weather as WeatherPreset)) {
    base.weather = data.weather as WeatherPreset;
  }

  const cover = data.groundCover as Record<string, unknown> | undefined;
  if (cover) {
    base.groundCover.snow = cover.snow === true;
    base.groundCover.ice = cover.ice === true;
    // An unknown variant id keeps the default rather than failing the load: a swatch that has been
    // renamed or retired should cost the choice of snow, not the project.
    if (isSnowSwatchId(cover.snowSwatch)) base.groundCover.snowSwatch = cover.snowSwatch;
    if (isIceSwatchId(cover.iceSwatch)) base.groundCover.iceSwatch = cover.iceSwatch;
    // Full cover on anything unreadable, rather than `clamp`'s zero: a cover that is switched on and
    // renders nothing at all is the one outcome with no honest reading.
    const amount = (value: unknown): number => {
      const parsed = Number(value ?? 1);
      return Number.isFinite(parsed) ? clamp(parsed, 0, 1) : 1;
    };
    base.groundCover.snowAmount = amount(cover.snowAmount);
    base.groundCover.iceAmount = amount(cover.iceAmount);
  }

  if (data.latitudeDegrees !== undefined) {
    base.latitudeDegrees = clamp(Number(data.latitudeDegrees), -90, 90);
  }

  return base;
}
